// ============================================
// Socket.io Client Service
// ============================================

import { io } from 'socket.io-client';
import { API_BASE_URL } from '../constants';

// Derive socket connection config from API_BASE_URL
// If API_BASE_URL is relative (e.g. '/api'), we're behind a reverse proxy (nginx)
// Socket.IO must route through the same /api prefix that nginx already proxies
const isRelativeUrl = API_BASE_URL.startsWith('/');
const SOCKET_URL = isRelativeUrl
    ? window.location.origin  // Connect to same origin (nginx will proxy)
    : API_BASE_URL.replace('/api', ''); // Direct connection (dev mode)

// Custom socket.io path: route through the /api proxy so nginx forwards to Node.js
const SOCKET_PATH = isRelativeUrl ? '/api/socket.io' : '/socket.io';

class SocketService {
    constructor() {
        this.socket = io(SOCKET_URL, {
            path: SOCKET_PATH,
            autoConnect: false,
            // Let socket.io use its default: start with polling, upgrade to websocket
            // DO NOT force transports: ['websocket'] — it breaks behind proxies/LBs
            reconnection: true,
            reconnectionAttempts: Infinity,
            reconnectionDelay: 1000,
            reconnectionDelayMax: 5000,
        });

        this.socket.on('connect', () => {
            console.log('🔌 Socket Connected:', this.socket.id, '| transport:', this.socket.io.engine.transport.name);
            if (this.role) this.socket.emit('join:role', this.role);
            if (this.userId) this.socket.emit('join:user', this.userId);
        });

        this.socket.io.on('reconnect', (attempt) => {
            console.log(`🔌 Socket Reconnected after ${attempt} attempts`);
            if (this.role) this.socket.emit('join:role', this.role);
            if (this.userId) this.socket.emit('join:user', this.userId);
        });

        this.socket.on('connect_error', (err) => {
            console.error('🔌 Socket Connection Error:', err.message);
        });

        this.socket.on('disconnect', (reason) => {
            console.log('🔌 Socket Disconnected:', reason);
        });
    }

    connect(role, userId) {
        this.role = role;
        this.userId = userId;

        // Attach auth token so server can verify identity
        const tokenKeys = { admin: 'admin_token', dispatcher: 'dispatcher_token', driver: 'driver_token', pa: 'pa_token', user: 'token' };
        const tokenKey = tokenKeys[role] || 'token';
        const token = localStorage.getItem(tokenKey);
        if (token) {
            this.socket.auth = { token };
        }

        if (!this.socket.connected) {
            this.socket.connect();
        } else {
            // Already connected — just (re-)join rooms
            if (this.role) this.socket.emit('join:role', this.role);
            if (this.userId) this.socket.emit('join:user', this.userId);
        }
    }

    disconnect() {
        if (this.socket.connected) {
            this.socket.disconnect();
        }
    }

    /** Force re-emit room join events (useful after reconnection edge cases) */
    forceRejoin() {
        if (this.socket.connected) {
            if (this.role) this.socket.emit('join:role', this.role);
            if (this.userId) this.socket.emit('join:user', this.userId);
        }
    }

    isConnected() {
        return this.socket.connected;
    }

    getSocketId() {
        return this.socket.id || null;
    }

    emit(event, data) {
        this.socket.emit(event, data);
    }

    on(event, callback) {
        this.socket.on(event, callback);
    }

    off(event, callback) {
        if (callback) {
            this.socket.off(event, callback);
        } else {
            this.socket.off(event);
        }
    }
}

const socketService = new SocketService();
export default socketService;
